import { useCallback, useEffect, useRef, useState } from 'react';
import { api, type WaGroup, type WaSession, type WaSessionStatus } from '../api.js';

const STATUS: Record<WaSessionStatus, { cls: string; label: string }> = {
  IDLE: { cls: '', label: 'aparelho offline' },
  CONNECTING: { cls: 'warn', label: 'conectando aparelho' },
  QR: { cls: 'warn', label: 'aguardando leitura do QR' },
  CONNECTED: { cls: 'ok', label: 'aparelho conectado' },
  LOGGED_OUT: { cls: 'err', label: 'pareamento removido' },
  BUSY: { cls: 'err', label: 'em uso em outro processo' },
  ERROR: { cls: 'err', label: 'aparelho com erro' },
};

/** Estados em que vale continuar perguntando: o desfecho ainda vai mudar. */
const TRANSIENT: WaSessionStatus[] = ['CONNECTING', 'QR'];

type Props = {
  /** grupo já gravado nas credenciais, para marcar o selecionado */
  selectedJid: string | null;
  selectedName: string | null;
  /** devolve true quando o grupo foi realmente gravado */
  onSelectGroup: (group: WaGroup) => Promise<boolean>;
};

export function WhatsappSession({ selectedJid, selectedName, onSelectGroup }: Props) {
  const [session, setSession] = useState<WaSession | null>(null);
  const [groups, setGroups] = useState<WaGroup[] | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [filtro, setFiltro] = useState('');
  const [error, setError] = useState<string | null>(null);
  const timer = useRef<number | null>(null);

  const stopPolling = useCallback(() => {
    if (timer.current !== null) {
      window.clearInterval(timer.current);
      timer.current = null;
    }
  }, []);

  const refresh = useCallback(async () => {
    try {
      const s = await api.waSessionStatus();
      setSession(s);
      if (!TRANSIENT.includes(s.status)) stopPolling();
      return s;
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : 'Falha ao ler o estado');
      stopPolling();
      return null;
    }
  }, [stopPolling]);

  // O QR do WhatsApp vence em cerca de 60s e o servidor gera outro sozinho.
  // O polling não serve só para detectar a conexão: é ele que troca a imagem
  // vencida na tela, senão o usuário miraria num QR que já não vale.
  const startPolling = useCallback(() => {
    stopPolling();
    // 1s para a contagem regressiva do QR não pular segundos na tela.
    timer.current = window.setInterval(() => void refresh(), 1000);
  }, [refresh, stopPolling]);

  useEffect(() => {
    void refresh();
    return stopPolling;
  }, [refresh, stopPolling]);

  async function connect(fresh = false) {
    setBusy('connect');
    setError(null);
    try {
      // Código expirado: o socket atual já não emite outro. Fechar antes de
      // abrir é o que garante um código novo em vez do mesmo morto.
      if (fresh) await api.waSessionPause();
      const s = await api.waSessionStart();
      setSession(s);
      if (TRANSIENT.includes(s.status)) startPolling();
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : 'Falha ao conectar');
    } finally {
      setBusy(null);
    }
  }

  async function loadGroups() {
    setBusy('groups');
    setError(null);
    setGroups(null);
    setFiltro('');
    try {
      const r = await api.waSessionGroups();
      setGroups(r.groups);
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : 'Falha ao listar grupos');
    } finally {
      setBusy(null);
    }
  }

  async function swapNumber() {
    const ok = window.confirm(
      'Isto desconecta o WhatsApp e apaga o pareamento. Você vai precisar ler um ' +
        'QR code novo — com o número que quiser usar daqui em diante. Continuar?',
    );
    if (!ok) return;
    setBusy('logout');
    setError(null);
    try {
      setSession(await api.waSessionLogout());
      setGroups(null);
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : 'Falha ao desconectar');
    } finally {
      setBusy(null);
    }
  }

  const status = session?.status ?? 'IDLE';
  const badge = STATUS[status];
  // Zero significa expirado de verdade: o servidor só devolve o campo enquanto
  // existe um código na tela.
  const expired = status === 'QR' && (session?.qrExpiresInSec ?? 1) <= 0;

  const termo = filtro.trim().toLocaleLowerCase('pt-BR');
  const visiveis = (groups ?? []).filter(
    (g) => !termo || g.name.toLocaleLowerCase('pt-BR').includes(termo),
  );

  return (
    <div className="form-grid" style={{ gap: 12 }}>
      <div
        style={{
          background: 'var(--warn-dim)',
          color: 'var(--warn)',
          border: '1px solid currentcolor',
          borderRadius: 'var(--radius)',
          padding: '10px 12px',
          fontSize: 13,
          lineHeight: 1.5,
        }}
      >
        <strong>Este modo viola os termos de uso do WhatsApp.</strong> Ele conecta como
        se fosse um aparelho seu, e a Meta identifica isso na própria conexão — não só
        no que é enviado. O risco maior é para quem dispara a desconhecidos; um post
        por dia num grupo em que as pessoas entraram por vontade própria fica na ponta
        mais branda. Ainda assim,{' '}
        <strong>use um chip separado, não o número principal do seu negócio.</strong> Se
        o número for bloqueado, você perde o grupo, os contatos e o histórico dele.
      </div>

      <div className="row" style={{ alignItems: 'center', gap: 10 }}>
        <span className={`badge ${badge.cls}`} style={{ flex: '0 0 auto' }}>
          {badge.label}
        </span>
        {session?.phone && (
          <span className="muted" style={{ flex: '0 0 auto', fontSize: 13 }}>
            número +{session.phone}
          </span>
        )}
        {session?.paired && status !== 'CONNECTED' && (
          <span className="muted" style={{ flex: '0 0 auto', fontSize: 13 }}>
            pareamento salvo — reconectar não pede QR
          </span>
        )}
      </div>

      {/* O erro de uma ação (listar grupos, escolher grupo) precisa aparecer
          mesmo com a sessão conectada — senão o clique falha em silêncio. Já o
          lastError da sessão só interessa quando ela NÃO está de pé. */}
      {(error ?? (status !== 'CONNECTED' ? session?.lastError : null)) && (
        <p className="err" style={{ fontSize: 13, margin: 0 }}>
          {error ?? session?.lastError}
        </p>
      )}

      {status === 'QR' && session?.qr && (
        <div className="row" style={{ alignItems: 'center', gap: 16 }}>
          <div style={{ flex: '0 0 auto', position: 'relative' }}>
            <img
              src={session.qr}
              alt="QR code para conectar o WhatsApp"
              width={220}
              height={220}
              style={{
                display: 'block',
                borderRadius: 'var(--radius)',
                background: '#fff',
                padding: 8,
                filter: expired ? 'blur(4px) grayscale(1)' : 'none',
              }}
            />
            {expired && (
              <button
                type="button"
                className="primary"
                style={{
                  position: 'absolute',
                  inset: '50% 8px auto 8px',
                  transform: 'translateY(-50%)',
                }}
                disabled={busy === 'connect'}
                onClick={() => void connect(true)}
              >
                {busy === 'connect' ? <span className="spinner" /> : 'Gerar novo código'}
              </button>
            )}
          </div>
          <div>
            <ol style={{ margin: 0, paddingLeft: 18, fontSize: 13, lineHeight: 1.7 }}>
              <li>Abra o WhatsApp no celular que vai postar</li>
              <li>
                Toque em <strong>Configurações › Aparelhos conectados</strong>
              </li>
              <li>
                Toque em <strong>Conectar um aparelho</strong> e aponte para o código
              </li>
            </ol>
            <p
              className={expired ? 'err' : 'muted'}
              style={{ fontSize: 12.5, margin: '10px 0 0' }}
            >
              {expired
                ? 'Este código expirou. Gere outro antes de escanear.'
                : `O código expira em ${session.qrExpiresInSec ?? 0}s — ele se renova sozinho.`}
            </p>
          </div>
        </div>
      )}

      <div className="row" style={{ gap: 8 }}>
        {status !== 'CONNECTED' && (
          <button
            type="button"
            className="primary"
            style={{ flex: '0 0 auto' }}
            disabled={busy === 'connect' || status === 'CONNECTING'}
            onClick={() => void connect(status === 'ERROR')}
          >
            {busy === 'connect' || status === 'CONNECTING' ? (
              <span className="spinner" />
            ) : session?.paired ? (
              'Reconectar'
            ) : (
              'Conectar aparelho'
            )}
          </button>
        )}

        {status === 'CONNECTED' && !selectedJid && (
          <button
            type="button"
            className="ghost"
            style={{ flex: '0 0 auto' }}
            disabled={busy === 'groups'}
            onClick={() => void loadGroups()}
          >
            {busy === 'groups' ? <span className="spinner" /> : 'Listar meus grupos'}
          </button>
        )}

        {(session?.paired || status === 'CONNECTED') && (
          <button
            type="button"
            className="danger"
            style={{ flex: '0 0 auto' }}
            disabled={busy === 'logout'}
            onClick={() => void swapNumber()}
          >
            {busy === 'logout' ? <span className="spinner" /> : 'Desconectar / trocar número'}
          </button>
        )}
      </div>

      {groups === null && selectedJid && (
        <div
          className="row"
          style={{
            alignItems: 'center',
            gap: 10,
            background: status === 'CONNECTED' ? 'var(--ok-dim)' : 'var(--surface-2)',
            color: status === 'CONNECTED' ? 'var(--ok)' : 'var(--text-dim)',
            borderRadius: 'var(--radius)',
            padding: '10px 12px',
            fontSize: 13,
          }}
        >
          <span style={{ flex: 1, minWidth: 0 }}>
            Publicando automaticamente em <strong>{selectedName ?? selectedJid}</strong>
            {status !== 'CONNECTED' && session?.paired && (
              <>
                <br />
                <span style={{ opacity: 0.8 }}>
                  O aparelho está offline agora, mas reconecta sozinho na hora de publicar.
                </span>
              </>
            )}
          </span>
          <button
            type="button"
            className="ghost"
            style={{ flex: '0 0 auto' }}
            // Listar grupos exige o socket de pé; sem conexão o botão só
            // devolveria erro.
            disabled={busy === 'groups' || status !== 'CONNECTED'}
            title={
              status === 'CONNECTED' ? undefined : 'Conecte o aparelho para trocar de grupo'
            }
            onClick={() => void loadGroups()}
          >
            {busy === 'groups' ? <span className="spinner" /> : 'Trocar grupo'}
          </button>
        </div>
      )}

      {groups && groups.length === 0 && (
        <p className="muted" style={{ fontSize: 13, margin: 0 }}>
          Este número não participa de nenhum grupo.
        </p>
      )}

      {groups && groups.length > 0 && (
        <div className="form-grid" style={{ gap: 6 }}>
          <label htmlFor="waFiltroGrupo">
            Grupo de destino{' '}
            <span className="muted" style={{ fontWeight: 400 }}>
              ({groups.length} grupos)
            </span>
          </label>
          {/* Uma conta real participa de dezenas de grupos; sem busca, achar o
              certo vira rolagem. */}
          {groups.length > 8 && (
            <input
              id="waFiltroGrupo"
              type="search"
              value={filtro}
              onChange={(e) => setFiltro(e.target.value)}
              placeholder="Buscar pelo nome do grupo…"
              autoComplete="off"
            />
          )}
          {selectedJid && (
            <button
              type="button"
              className="ghost"
              style={{ alignSelf: 'flex-start' }}
              onClick={() => {
                setGroups(null);
                setFiltro('');
              }}
            >
              Cancelar
            </button>
          )}
          {visiveis.length === 0 && (
            <p className="muted" style={{ fontSize: 13, margin: 0 }}>
              Nenhum grupo com “{filtro}”.
            </p>
          )}
          {visiveis.map((g) => {
            const chosen = g.jid === selectedJid;
            // Só desabilita quando o servidor tem certeza de que o envio seria
            // recusado. Em caso de dúvida deixamos tentar: bloquear na tela o
            // grupo que a pessoa mais quer usar é pior que uma falha explícita.
            const blocked = !g.canPost;
            return (
              <button
                key={g.jid}
                type="button"
                className={chosen ? 'primary' : 'ghost'}
                disabled={blocked || busy === `pick-${g.jid}`}
                style={{
                  justifyContent: 'space-between',
                  textAlign: 'left',
                  opacity: blocked ? 0.55 : 1,
                }}
                onClick={() => {
                  setBusy(`pick-${g.jid}`);
                  void onSelectGroup(g)
                    .then((gravou) => {
                      // Recolhe a lista só quando gravou. Recolher numa falha
                      // esconderia a lista mostrando um grupo que o worker não
                      // conhece.
                      if (gravou) {
                        setGroups(null);
                        setFiltro('');
                      }
                    })
                    .finally(() => setBusy(null));
                }}
              >
                <span>
                  {chosen ? '✓ ' : ''}
                  {g.name}
                </span>
                <span style={{ fontSize: 12, opacity: 0.75 }}>
                  {g.participants} membros
                  {g.isAdmin === true ? ' · você é admin' : ''}
                  {blocked ? ' · só admins podem postar' : ''}
                </span>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
